/**
 * SectionNavRail — floating scroll-reactive section navigation.
 *
 * Fixed to the right edge of the viewport, vertically centered.
 * Uses IntersectionObserver (viewport root) to highlight the active section as the user scrolls.
 * Click any row to smooth-scroll to that section.
 *
 * Hidden on screens < 1280px (needs margin space).
 *
 * Dynamically reads section order from the DOM (data-section-nav attributes)
 * so the rail always matches the actual page structure from the DocViewSchema.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { RIGHT_RAIL_WIDTH } from "@/components/layout/AppRailRight";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";

// ─── Section metadata lookup (English label by nav id) ───

const SECTION_META: Record<string, string> = {
  "product-details": "Product Details",
  "item-overview": "Product Images & Overview",
  "pricing": "Pricing",
  "storefront": "Storefront",
  "documents": "Product Documents",
  "collections": "Collections",
  "marketplace-listings": "Marketplace Listings",
  "inventory": "Inventory Settings",
  "advanced": "Advanced",
};

// ─── Scroll spy hook ───

interface VisibleSection {
  id: string;
  label: string;
}

function useScrollSpy() {
  const [active, setActive] = useState("");
  const [orderedSections, setOrderedSections] = useState<VisibleSection[]>([]);
  const isClickScrolling = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const knownIdsRef = useRef<string>("");

  const setupObserver = useCallback(() => {
    // Clean up previous observer
    observerRef.current?.disconnect();

    // Query all data-section-nav elements in DOM order
    const els = document.querySelectorAll<HTMLElement>("[data-section-nav]");
    const sections: VisibleSection[] = [];
    const elements: Element[] = [];

    els.forEach((el) => {
      const id = el.dataset.sectionNav;
      if (!id) return;
      const label = SECTION_META[id];
      if (!label) return; // skip sections we don't have metadata for
      sections.push({ id, label });
      elements.push(el);
    });

    if (sections.length === 0) return;

    // Only update state if sections changed (avoid unnecessary re-renders)
    const newIds = sections.map((s) => s.id).join(",");
    if (newIds !== knownIdsRef.current) {
      knownIdsRef.current = newIds;
      setOrderedSections(sections);
      if (!active || !sections.some((s) => s.id === active)) {
        setActive(sections[0].id);
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;
        let best: { id: string; ratio: number } | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const sectionId = (entry.target as HTMLElement).dataset.sectionNav;
            if (sectionId && (!best || entry.intersectionRatio > best.ratio)) {
              best = { id: sectionId, ratio: entry.intersectionRatio };
            }
          }
        }
        if (best) setActive(best.id);
      },
      { root: null, rootMargin: "0px 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    for (const el of elements) {
      observer.observe(el);
    }
    observerRef.current = observer;
  }, [active]);

  useEffect(() => {
    setupObserver();

    // Watch for DOM changes — sections load asynchronously via ExperienceView
    const mutationObserver = new MutationObserver(() => {
      // Re-scan if any data-section-nav elements changed
      const els = document.querySelectorAll<HTMLElement>("[data-section-nav]");
      const currentIds = Array.from(els)
        .map((el) => el.dataset.sectionNav)
        .filter(Boolean)
        .join(",");
      if (currentIds !== knownIdsRef.current) {
        setupObserver();
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observerRef.current?.disconnect();
      mutationObserver.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = useCallback(
    (id: string) => {
      const el = document.querySelector(`[data-section-nav="${id}"]`) as HTMLElement | null;
      if (!el) return;
      isClickScrolling.current = true;
      setActive(id);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => { isClickScrolling.current = false; }, 800);
    },
    []
  );

  return { active, scrollTo, orderedSections };
}

// ─── Layout constants (shared with ItemDetailPage for content offset) ───

const RAIL_WIDTH = 120;
const RAIL_GAP = 24; // visual gap between rail and app sidebar
/** Total space the rail occupies — used by ItemDetailPage to offset content centering */
export const RAIL_FOOTPRINT = RAIL_WIDTH + RAIL_GAP;
/** Right offset: clears the 48px AppRailRight + 24px visual gap */
const RAIL_RIGHT = RIGHT_RAIL_WIDTH + RAIL_GAP;

// ─── Component ───

export function SectionNavRail() {
  const { active, scrollTo, orderedSections } = useScrollSpy();
  const { language } = useLanguage();

  // Don't render if fewer than 2 sections are in the DOM
  if (orderedSections.length < 2) return null;

  return (
    <div
      className="hidden xl:flex fixed top-1/2 -translate-y-1/2 z-20 flex-col gap-0.5"
      style={{ right: RAIL_RIGHT, width: RAIL_WIDTH }}
    >
      {orderedSections.map((s) => {
        const isActive = active === s.id;
        const translatedLabel = getSectionTitleTranslation(s.label, language);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollTo(s.id)}
            className={cn(
              "flex items-center gap-2 py-1.5 px-2.5 rounded-sm text-left transition-colors duration-200",
              isActive
                ? "text-foreground"
                : "text-muted-foreground/40 hover:text-muted-foreground/70"
            )}
          >
            <div
              className={cn(
                "w-1 rounded-full shrink-0 transition-all duration-200",
                isActive ? "h-3.5 bg-foreground/70" : "h-2 bg-muted-foreground/20"
              )}
            />
            <span
              className={cn(
                "text-[12px] truncate transition-all duration-200",
                isActive ? "font-medium" : "font-normal"
              )}
            >
              {translatedLabel}
            </span>
          </button>
        );
      })}
    </div>
  );
}
